import React, { useState, useRef, useEffect } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useOtcProviders, useCreateOtcProvider } from "../hooks";
import { OtcSettlementProvider } from "../types";

export interface SettlementMethodDropdownProps {
  selectedProviderId: string;
  onSelectProvider: (providerId: string) => void;
  currencyFrom?: string;
  currencyTo?: string;
  disabled?: boolean;
}

export const SettlementMethodDropdown: React.FC<SettlementMethodDropdownProps> = ({
  selectedProviderId,
  onSelectProvider,
  currencyFrom = "USD",
  currencyTo = "NGN",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState<"external" | "internal">("external");
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    providers,
    isLoading: isLoadingProviders,
    refetch: refetchProviders,
  } = useOtcProviders({
    currencyFrom,
    currencyTo,
    enabled: !disabled,
  });

  const createProviderMutation = useCreateOtcProvider();

  // Auto-select first provider if none selected
  useEffect(() => {
    if (!selectedProviderId && providers.length > 0) {
      const firstId = providers[0]._id || providers[0].id;
      if (firstId) {
        onSelectProvider(firstId);
      }
    }
  }, [providers, selectedProviderId, onSelectProvider]);

  // Reset adding state whenever dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setIsAdding(false);
      setNewName("");
      setNewType("external");
    }
  }, [isOpen]);

  // Focus input when adding
  useEffect(() => {
    if (isAdding) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [isAdding]);

  const selectedProvider =
    providers.find(
      (prov: OtcSettlementProvider) =>
        (prov._id && prov._id === selectedProviderId) ||
        (prov.id && prov.id === selectedProviderId)
    ) || (providers.length > 0 ? providers[0] : undefined);

  const activeProviderName = selectedProvider?.name
    ? selectedProvider.name
    : isLoadingProviders
    ? "Loading providers..."
    : "Select provider";

  const handleCreateProvider = async () => {
    const trimmed = newName.trim();
    if (!trimmed || createProviderMutation.isPending) return;

    try {
      const created = await createProviderMutation.mutateAsync({
        name: trimmed,
        currencyFrom,
        currencyTo,
      });

      toast.success(`Settlement method "${trimmed}" added successfully`);
      const newId = created.id || created._id;
      if (newId) {
        onSelectProvider(newId);
      }
      refetchProviders();
      setIsAdding(false);
      setNewName("");
      setIsOpen(false);
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status?: number; data?: { message?: string } };
        message?: string;
      };
      const apiMsg = axiosErr?.response?.data?.message || axiosErr?.message || "Failed to add settlement method";
      
      // Fallback: If endpoint is not found or failed, still allow testing by selecting locally
      toast.error(apiMsg);
    }
  };

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild disabled={disabled || isLoadingProviders}>
        <button
          type="button"
          disabled={disabled || isLoadingProviders}
          className="flex h-[48px] w-full items-center justify-between rounded-[8px] bg-[#F2F4F7] px-3 text-left transition-colors hover:bg-[#EAECF0] focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <span className="text-[13px] capitalize font-normal text-[#101828] truncate">
            {activeProviderName}
          </span>
          <ChevronDown size={16} className="text-[#667085] shrink-0" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          onCloseAutoFocus={(e) => {
            if (isAdding) e.preventDefault();
          }}
          className="z-[150] w-[var(--radix-dropdown-menu-trigger-width)] min-w-[280px] max-h-[320px] overflow-y-auto rounded-[12px] border border-[#E4E7EC] bg-white p-2 shadow-lg"
        >
          {isLoadingProviders && providers.length === 0 ? (
            <div className="p-3 text-center text-[12px] text-[#667085]">
              Loading providers...
            </div>
          ) : providers.length === 0 && !isAdding ? (
            <div className="p-3 text-center text-[12px] text-[#667085]">
              No settlement providers found for {currencyFrom} → {currencyTo}
            </div>
          ) : (
            <div className="flex flex-col gap-0.5">
              {providers.map((provider: OtcSettlementProvider) => {
                const provId = provider._id || provider.id;
                const isSelected =
                  selectedProvider?._id === provId ||
                  selectedProvider?.id === provId ||
                  selectedProviderId === provId;

                const rawType = (provider.type || "external").toLowerCase();
                const typeLabel = `${
                  rawType.charAt(0).toUpperCase() + rawType.slice(1)
                } transfer`;

                return (
                  <DropdownMenu.Item
                    key={provId}
                    onSelect={() => {
                      onSelectProvider(provId);
                    }}
                    className={`flex flex-col cursor-pointer rounded-[8px] px-3 py-2 text-left outline-none transition-colors ${
                      isSelected ? "bg-[#F4F5FA]" : "hover:bg-[#F9FAFB]"
                    }`}
                  >
                    <span className="text-[14px] font-medium text-[#101828]">
                      {provider.name}
                    </span>
                    <span className="text-[12px] text-[#667085]">
                      {typeLabel}
                    </span>
                  </DropdownMenu.Item>
                );
              })}
            </div>
          )}

          {/* Inline Add New Method row (settlement-method_new) */}
          {isAdding && (
            <div
              className="mt-1 flex items-center gap-1.5 px-1 py-1"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <div className="flex flex-1 items-center justify-between rounded-[8px] border border-[#D6BBFB] bg-[#FDFBF9] px-3 py-1.5 focus-within:ring-2 focus-within:ring-[#D6BBFB]/60">
                <input
                  ref={inputRef}
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleCreateProvider();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      setIsAdding(false);
                      setNewName("");
                    }
                  }}
                  placeholder="Provider name"
                  disabled={createProviderMutation.isPending}
                  className="w-full text-[13px] font-normal text-[#101828] placeholder:text-[#98A2B3] bg-transparent outline-none"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    setNewType((prev) =>
                      prev === "external" ? "internal" : "external"
                    );
                  }}
                  title="Click to toggle transfer type"
                  className="ml-2 shrink-0 text-[12px] font-normal text-[#475467] hover:text-[#101828] select-none"
                >
                  {newType === "external" ? "External" : "Internal"}
                </button>
              </div>

              <div className="flex items-center shrink-0">
                <button
                  type="button"
                  disabled={!newName.trim() || createProviderMutation.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateProvider();
                  }}
                  className="rounded-[6px] p-1.5 text-[#344054] hover:bg-[#F2F4F7] hover:text-[#101828] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Confirm"
                >
                  {createProviderMutation.isPending ? (
                    <Loader2 size={16} className="animate-spin text-[#7F56D9]" />
                  ) : (
                    <Check size={16} />
                  )}
                </button>
                <button
                  type="button"
                  disabled={createProviderMutation.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsAdding(false);
                    setNewName("");
                  }}
                  className="rounded-[6px] p-1.5 text-[#344054] hover:bg-[#F2F4F7] hover:text-[#101828] transition-colors"
                  title="Cancel"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Action to show inline add row */}
          {isAdding ? (
            <div className="px-3 py-2 text-[13px] font-normal text-[#98A2B3] select-none cursor-not-allowed mt-1">
              + Add new method
            </div>
          ) : (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsAdding(true);
              }}
              className="mt-1 flex w-full items-center rounded-[8px] px-3 py-2 text-left text-[13px] font-medium text-[#6941C6] hover:bg-[#F9FAFB] hover:text-[#53389E] transition-colors"
            >
              + Add new method
            </button>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
